import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import { useSubscription } from '../../context/SubscriptionContext';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { refreshSubscription } = useSubscription();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Refresh subscription data after successful payment
    if (sessionId) {
      // Give Stripe webhook a moment to process
      const timer = setTimeout(() => {
        refreshSubscription();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, refreshSubscription]);

  return (
    <PublicLayout>
      <div className="py-24 sm:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Thank you for your subscription. Your account has been upgraded and you now have
            access to all premium features.
          </p>

          {/* What's Next */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h2>
            <ul className="text-left text-gray-600 space-y-3">
              <li className="flex items-start">
                <svg
                  className="flex-shrink-0 w-5 h-5 text-green-500 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="ml-3">
                  Start recording immediately with unlimited attempts
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="flex-shrink-0 w-5 h-5 text-green-500 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="ml-3">
                  Access detailed phoneme-level analysis for each recording
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="flex-shrink-0 w-5 h-5 text-green-500 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="ml-3">
                  Get personalized tips to improve your pronunciation
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="flex-shrink-0 w-5 h-5 text-green-500 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="ml-3">
                  A confirmation email has been sent to your address
                </span>
              </li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Start Practicing Now
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Manage Subscription
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PaymentSuccessPage;
